import * as React from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import moment from 'moment-timezone';
import { Table, Input, Button, Dropdown, Space, Modal, message } from 'antd';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useDimensions } from '@src/services/queries';
import { useDeleteDimension } from '@src/services/mutations';
import { useManagementContext } from './context';
import AssociatedFunnelAnalyses from './AssociatedFunnelAnalyses';
import * as Styles from './styled';

const Dimensions = () => {
  const navigate = useNavigate();
  const { search: querySearch } = useLocation();
  const { setEditingPanelOpen } = useManagementContext();
  const { data, isFetching, refetch } = useDimensions({
    staleTime: 0,
  });
  const { mutateAsync: deleteDimension } = useDeleteDimension();
  const [search, setSearch] = React.useState<string>();
  const [loadingIds, setLoadingIds] = React.useState<string[]>([]);
  const loadingIdSet = React.useMemo(() => new Set(loadingIds), [loadingIds]);
  const [modal, contextHolder] = Modal.useModal();

  type DataItem = NonNullable<typeof data>['dimensions'][0];

  const columns = React.useMemo<GetProp<typeof Table<DataItem>, 'columns'>>(
    () => [
      {
        title: 'Dimension Name',
        dataIndex: ['name'],
        render: (value: DataItem['name'], record) =>
          record.predefined ? (
            value
          ) : (
            <NavLink
              to={`dimensions/${record.id}${querySearch}`}
              onClick={() => setEditingPanelOpen(true)}
            >
              {value}
            </NavLink>
          ),
        sorter: (a, b) => {
          return (a.name ?? '').localeCompare(b.name ?? '');
        },
      },
      {
        title: 'Tag Key',
        dataIndex: ['rules'],
        render: (value: DataItem['rules']) => {
          const tagKeys = value.flat().map((rule) => rule.tag_key);

          return tagKeys.join(', ');
        },
      },
      {
        title: 'Updated By',
        dataIndex: 'updated_by',
        sorter: (a, b) => {
          return (a.updated_by ?? '').localeCompare(b.updated_by ?? '');
        },
      },
      {
        title: 'Last Modified',
        dataIndex: 'updated_at',
        render: (value: DataItem['updated_at']) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
        sorter: (a, b) => {
          return moment(a.updated_at).unix() - moment(b.updated_at).unix();
        },
        defaultSortOrder: 'descend',
      },
      {
        title: 'Actions',
        dataIndex: 'id',
        render: (value: DataItem['id'], record) => {
          return (
            <Dropdown
              disabled={loadingIdSet.has(value)}
              menu={{
                items: [
                  { key: 'edit', label: 'Edit', disabled: record.predefined },
                  {
                    key: 'delete',
                    label: 'Delete',
                    disabled: record.predefined,
                  },
                ],
                onClick: ({ key }) => {
                  if (key === 'edit') {
                    void navigate({
                      pathname: `dimensions/${value}`,
                      search: querySearch,
                    });
                    setEditingPanelOpen(true);
                  }

                  if (key === 'delete') {
                    const confirmInstance = modal.confirm({
                      title: 'Are you sure you want to delete this Dimension?',
                      content: <AssociatedFunnelAnalyses type="dimensions" id={value} />,
                      okButtonProps: { danger: true, type: 'primary' },
                      okText: 'Delete',
                      width: 560,
                      onOk: async () => {
                        setLoadingIds((prev) => [...prev, value]);
                        confirmInstance.update({ cancelButtonProps: { disabled: true } });

                        await deleteDimension(value)
                          .then(() => {
                            message.success('Dimension deleted successfully');
                          })
                          .catch((error) => {
                            message.error((error as Error).message ?? 'Failed to delete Dimension');
                          })
                          .finally(() => {
                            setLoadingIds((prev) => prev.filter((id) => id !== value));
                          })
                          .then(() => refetch());
                      },
                    });
                  }
                },
              }}
            >
              <Button loading={loadingIdSet.has(value)} icon={<MoreOutlined />} type="text" />
            </Dropdown>
          );
        },
      },
    ],
    [querySearch, setEditingPanelOpen, loadingIdSet, navigate, deleteDimension, refetch, modal],
  );

  const tableData = React.useMemo(() => {
    if (!data?.dimensions || !search) {
      return data?.dimensions;
    }

    return data.dimensions.filter(
      (item) =>
        item.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.updated_by?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [data, search]);

  return (
    <>
      <Space direction="vertical" size={16} style={{ display: 'flex' }}>
        <Space size={16} align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Styles.TableCount>Total {data?.dimensions.length ?? 0} Dimensions</Styles.TableCount>
          <Button
            variant="outlined"
            color="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              void navigate({
                pathname: `dimensions`,
                search: querySearch,
              });
              setEditingPanelOpen(true);
            }}
          >
            Dimension
          </Button>
          <Input.Search allowClear placeholder="Search..." onSearch={setSearch} />
        </Space>
        <Table
          rowKey="id"
          dataSource={tableData}
          columns={columns}
          loading={isFetching}
          scroll={{ x: true }}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
          }}
          sortDirections={['ascend', 'descend', 'ascend']}
        />
      </Space>
      {contextHolder}
    </>
  );
};

export default React.memo(Dimensions);
